import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, Grid,
  TextField, Button, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Alert, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Refresh as RefreshIcon, Add as AddIcon } from '@mui/icons-material';
import { api, apiRoutes } from '../../../api';
import { MapContainer, TileLayer, Marker, GeoJSON, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons for Leaflet in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Surveys = () => {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
  const [surveys, setSurveys] = useState([]);
  const [developerCampaigns, setDeveloperCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [neighborhoodBoundary, setNeighborhoodBoundary] = useState(null);
  const [mapCenter, setMapCenter] = useState([35.6892, 51.389]);
  const [mapKey, setMapKey] = useState(0);
  const [summary, setSummary] = useState([]);
  const [formData, setFormData] = useState({
    location: '',
    survey_data: {
      question1: '',
      question2: '',
      question3: '',
      question4: '',
      question5: ''
    },
    resident_info: {
      name: '',
      age: '',
      gender: '',
      contact: ''
    }
  });

  useEffect(() => {
    loadAgentNeighborhoods();
    loadSurveys();
    loadDeveloperCampaigns();
  }, []);

  useEffect(() => {
    if (selectedNeighborhood) {
      loadNeighborhoodBoundary(selectedNeighborhood);
    }
  }, [selectedNeighborhood]);

  const loadAgentNeighborhoods = async () => {
    try {
      const token = localStorage.getItem('access_token');

      const agentResponse = await api.get(apiRoutes.agents.profile, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!agentResponse.data?.neighborhoods) {
        throw new Error('Failed to load agent neighborhoods');
      }

      setNeighborhoods(agentResponse.data.neighborhoods);

      if (agentResponse.data.neighborhoods.length > 0) {
        setSelectedNeighborhood(agentResponse.data.neighborhoods[0].id);
      }
    } catch (err) {
      setError('خطا در بارگذاری محله‌های تخصیص یافته');
    } finally {
      setLoading(false);
    }
  };

  const loadSurveys = async () => {
    try {
      const token = localStorage.getItem('access_token');

      const response = await api.get(apiRoutes.surveys.list, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle paginated response (results array) or direct array response
      const surveysData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];

      setSurveys(surveysData);
      // Build per-neighborhood summary for assigned neighborhoods
      const counts = {};
      const lastDates = {};
      surveysData.forEach((s) => {
        const nid = s.neighborhood || s.neighborhood_id || s.neighborhoodId || 'unknown';
        if (!counts[nid]) counts[nid] = 0;
        counts[nid] += 1;
        const created = s.created_at ? new Date(s.created_at) : null;
        if (created) {
          if (!lastDates[nid] || created > lastDates[nid]) lastDates[nid] = created;
        }
      });
      setSummary(Object.keys(counts).map((nid) => ({
        neighborhoodId: nid,
        count: counts[nid],
        lastDate: lastDates[nid] || null,
      })));
    } catch (err) {

      setSurveys([]); // Ensure surveys is always an array
    }
  };

  const loadDeveloperCampaigns = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(apiRoutes.developers.surveyCampaigns, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const campaignsData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
      setDeveloperCampaigns(campaignsData);
    } catch (err) {
      setDeveloperCampaigns([]);
    }
  };

  const loadNeighborhoodBoundary = async (neighborhoodId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(apiRoutes.locations.neighborhoodBoundary(neighborhoodId), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setNeighborhoodBoundary(data.boundary || null);
      if (data.center) {
        setMapCenter([data.center.lat, data.center.lon]);
      }
      setMapKey((k) => k + 1);
    } catch (err) {

      setNeighborhoodBoundary(null);
    }
  };

  const handleNeighborhoodChange = (event) => {
    setSelectedNeighborhood(event.target.value);
  };

  const handleOpenDialog = () => {
    setFormData({
      location: '',
      survey_data: {
        question1: '',
        question2: '',
        question3: '',
        question4: '',
        question5: ''
      },
      resident_info: {
        name: '',
        age: '',
        gender: '',
        contact: ''
      }
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');

      // Build GeoJSON Point if map coordinate string is provided like "lat,lon"
      let locationPayload = formData.location;
      if (typeof formData.location === 'string' && formData.location.includes(',')) {
        const parts = formData.location.split(',').map((p) => parseFloat(p.trim()));
        if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
          const lat = parts[0];
          const lon = parts[1];
          locationPayload = { type: 'Point', coordinates: [lon, lat] };
        }
      }

      const payload = {
        neighborhood: selectedNeighborhood,
        location: locationPayload,
        survey_data: formData.survey_data,
        resident_info: formData.resident_info
      };

      await api.post(apiRoutes.surveys.list, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await loadSurveys();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.error || 'خطا در ذخیره مصاحبه');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSurveyDataChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      survey_data: {
        ...prev.survey_data,
        [name]: value
      }
    }));
  };

  const handleResidentInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      resident_info: {
        ...prev.resident_info,
        [name]: value
      }
    }));
  };

  const getGenderLabel = (value) => {
    const labels = {
      male: 'مرد',
      female: 'زن',
      other: 'سایر'
    };
    return labels[value] || '-';
  };

  const ClickSelector = ({ onSelect }) => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        onSelect({ lat, lon: lng });
      }
    });
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box dir="rtl" className="agents-section agents-surveys" sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box
        className="agents-section-head"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={{ borderBottom: '1px solid #ddd', pb: 1 }}
      >
        <Typography variant="h5" component="h1" fontWeight="700">
          مصاحبه‌های کیفیت زندگی
        </Typography>
        <Box>
          <Tooltip title="بارگذاری مجدد مصاحبه‌ها">
            <IconButton
              aria-label="بارگذاری مجدد مصاحبه‌ها"
              onClick={() => {
                loadSurveys();
                loadDeveloperCampaigns();
              }}
              size="large"
              sx={{ mr: 1 }}
            >
              <RefreshIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            مصاحبه جدید
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} variant="outlined">
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table stickyHeader size="small" aria-label="کمپین‌های نظرسنجی پروژه">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>کمپین پروژه سازنده</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>وضعیت</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">دعوت‌ها</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">پاسخ‌ها</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>شروع</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {developerCampaigns.map((campaign) => (
                    <TableRow key={campaign.id} hover>
                      <TableCell>{campaign.name}</TableCell>
                      <TableCell>{campaign.status}</TableCell>
                      <TableCell align="right">{campaign.invitations_count || 0}</TableCell>
                      <TableCell align="right">{campaign.responses_count || 0}</TableCell>
                      <TableCell>{campaign.opened_at ? new Date(campaign.opened_at).toLocaleDateString('fa-IR') : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {!developerCampaigns.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          کمپین نظرسنجی پروژه‌ای برای شما تخصیص داده نشده است
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Neighborhood Selection */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <FormControl fullWidth variant="outlined" size="medium">
              <InputLabel id="neighborhood-select-label">انتخاب محله</InputLabel>
              <Select
                labelId="neighborhood-select-label"
                value={selectedNeighborhood}
                onChange={handleNeighborhoodChange}
                label="انتخاب محله"
              >
                {neighborhoods.map((neighborhood) => (
                  <MenuItem key={neighborhood.id} value={neighborhood.id}>
                    {neighborhood.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Card>
        </Grid>

        {/* Summary Table per Neighborhood */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table stickyHeader size="small" aria-label="خلاصه مصاحبه‌ها">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>محله</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">تعداد مصاحبه</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">آخرین تاریخ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {neighborhoods.map((n) => {
                    const row = summary.find((s) => String(s.neighborhoodId) === String(n.id));
                    return (
                      <TableRow key={n.id} hover selected={String(selectedNeighborhood) === String(n.id)}>
                        <TableCell>{n.name}</TableCell>
                        <TableCell align="right">{row ? row.count : 0}</TableCell>
                        <TableCell align="right">{row && row.lastDate ? row.lastDate.toLocaleDateString('fa-IR') : '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Surveys Table */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table stickyHeader size="small" aria-label="جدول مصاحبه‌ها">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>محله</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>نام ساکن</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>سن</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>جنسیت</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>تاریخ مصاحبه</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>موقعیت</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(surveys) && surveys.map((survey) => (
                    <TableRow key={survey.id} hover>
                      <TableCell>{survey.neighborhood_name || '-'}</TableCell>
                      <TableCell>{survey.resident_info?.name || '-'}</TableCell>
                      <TableCell>{survey.resident_info?.age || '-'}</TableCell>
                      <TableCell>{getGenderLabel(survey.resident_info?.gender)}</TableCell>
                      <TableCell>
                        {survey.created_at ? new Date(survey.created_at).toLocaleDateString('fa-IR') : '-'}
                      </TableCell>
                      <TableCell>{survey.location || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {(!Array.isArray(surveys) || surveys.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          هیچ مصاحبه‌ای ثبت نشده است
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Survey Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="700">
            مصاحبه جدید کیفیت زندگی
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* Resident Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  اطلاعات ساکن
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="نام ساکن"
                  name="name"
                  value={formData.resident_info.name}
                  onChange={handleResidentInfoChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="سن"
                  name="age"
                  type="number"
                  value={formData.resident_info.age}
                  onChange={handleResidentInfoChange}
                  fullWidth
                  inputProps={{ min: 0, max: 120 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="gender-label">جنسیت</InputLabel>
                  <Select
                    labelId="gender-label"
                    name="gender"
                    value={formData.resident_info.gender}
                    onChange={handleResidentInfoChange}
                    label="جنسیت"
                  >
                    <MenuItem value=""><em>انتخاب کنید</em></MenuItem>
                    <MenuItem value="male">مرد</MenuItem>
                    <MenuItem value="female">زن</MenuItem>
                    <MenuItem value="other">سایر</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="اطلاعات تماس"
                  name="contact"
                  value={formData.resident_info.contact}
                  onChange={handleResidentInfoChange}
                  fullWidth
                />
              </Grid>

              {/* Survey Questions */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  پرسشنامه کیفیت زندگی
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="سوال ۱: میزان رضایت از محله"
                  name="question1"
                  value={formData.survey_data.question1}
                  onChange={handleSurveyDataChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="نظر خود را در مورد محله بنویسید..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="سوال ۲: کیفیت خدمات شهری"
                  name="question2"
                  value={formData.survey_data.question2}
                  onChange={handleSurveyDataChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="نظر خود را در مورد خدمات شهری بنویسید..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="سوال ۳: امنیت محله"
                  name="question3"
                  value={formData.survey_data.question3}
                  onChange={handleSurveyDataChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="نظر خود را در مورد امنیت محله بنویسید..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="سوال ۴: دسترسی به امکانات"
                  name="question4"
                  value={formData.survey_data.question4}
                  onChange={handleSurveyDataChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="نظر خود را در مورد دسترسی به امکانات بنویسید..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="سوال ۵: پیشنهادات و انتقادات"
                  name="question5"
                  value={formData.survey_data.question5}
                  onChange={handleSurveyDataChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="پیشنهادات و انتقادات خود را بنویسید..."
                />
              </Grid>

              {/* Location - Map Selector */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  انتخاب موقعیت روی نقشه
                </Typography>
                <Box sx={{ height: 400 }}>
                  <MapContainer key={mapKey} center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                    {neighborhoodBoundary && (
                      <GeoJSON data={neighborhoodBoundary} style={{ color: '#1976d2', weight: 2, fillColor: '#90caf9', fillOpacity: 0.2 }} />
                    )}
                    <ClickSelector onSelect={({ lat, lon }) => {
                      setFormData((prev) => ({
                        ...prev,
                        location: `${lat.toFixed(6)}, ${lon.toFixed(6)}`
                      }));
                    }} />
                    {typeof formData.location === 'string' && formData.location.includes(',') && (() => {
                      const parts = formData.location.split(',').map((p) => parseFloat(p.trim()));
                      if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
                        return <Marker position={[parts[0], parts[1]]} />;
                      }
                      return null;
                    })()}
                  </MapContainer>
                </Box>
                <Box mt={1}>
                  <TextField
                    label="مختصات انتخاب‌شده (lat, lon)"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    fullWidth
                    placeholder="مثال: 35.6892, 51.3890"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            انصراف
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'در حال ذخیره...' : 'ذخیره مصاحبه'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Surveys;
